import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Filter as FilterIcon, X, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import {
  getMovimentacoes,
  buildExportUrl,
  formatCNPJ,
  formatDateTime,
} from "@/lib/ocorenApi";
import { CATEGORIAS, classifyLocal, getCategoria } from "@/lib/categorias";
import { OcorrenciaDrawer } from "@/components/OcorrenciaDrawer";

const EMPTY_FILTERS = {
  cnpj_transportadora: "",
  cnpj_cliente: "",
  numero_nota: "",
  codigo_ocorrencia: "",
  descricao: "",
  categoria: "",
  data_inicio: "",
  data_fim: "",
  arquivo_id: "",
};

const statusAccent = (mov) => {
  const cat = classifyLocal(mov.codigo_ocorrencia, mov.descricao_ocorrencia);
  const c = getCategoria(cat);
  return {
    label: c.label,
    class: `${c.bg} ${c.text} border ${c.border}`,
  };
};

export default function Ocorrencias() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialArquivoId = searchParams.get("arquivo_id") || "";

  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    arquivo_id: initialArquivoId,
  });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await getMovimentacoes({ ...f, limit: 500 });
      setData(res);
    } catch {
      toast.error("Falha ao carregar movimentações");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    fetchData(filters);
    // sincroniza arquivo_id na URL
    if (filters.arquivo_id) {
      setSearchParams({ arquivo_id: filters.arquivo_id });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchParams({});
    fetchData(EMPTY_FILTERS);
  };

  const onChange = (k) => (e) => {
    setFilters((prev) => ({ ...prev, [k]: e.target.value }));
  };

  const onExport = () => {
    const url = buildExportUrl(filters);
    window.open(url, "_blank");
  };

  const openDetails = (mov) => {
    setSelected(mov);
    setDrawerOpen(true);
  };

  return (
    <div data-testid="page-ocorrencias" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Movimentações
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Ocorrências (NFs)
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            {data.total} movimentação(ões) encontrada(s).
          </p>
        </div>
        <button
          data-testid="export-excel-btn"
          onClick={onExport}
          className="bg-[#09090B] text-white hover:bg-[#27272A] px-5 py-2.5 text-sm font-semibold rounded-md flex items-center gap-2 transition-colors"
        >
          <Download size={15} />
          Exportar Excel
        </button>
      </div>

      {/* Filtros */}
      <div
        data-testid="filters-section"
        className="bg-white border border-[#E4E4E7] rounded-md p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <FilterIcon size={14} className="text-[#52525B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
            Filtros avançados
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Field
            label="CNPJ Transportadora"
            testId="filter-cnpj-transp"
            value={filters.cnpj_transportadora}
            onChange={onChange("cnpj_transportadora")}
            placeholder="00.000.000/0000-00"
          />
          <Field
            label="CNPJ Cliente"
            testId="filter-cnpj-cliente"
            value={filters.cnpj_cliente}
            onChange={onChange("cnpj_cliente")}
            placeholder="00.000.000/0000-00"
          />
          <Field
            label="Número NF"
            testId="filter-nf"
            value={filters.numero_nota}
            onChange={onChange("numero_nota")}
            placeholder="000123456"
          />
          <Field
            label="Código Ocorr."
            testId="filter-codigo"
            value={filters.codigo_ocorrencia}
            onChange={onChange("codigo_ocorrencia")}
            placeholder="01, 03..."
          />
          <Field
            label="Descrição contém"
            testId="filter-descricao"
            value={filters.descricao}
            onChange={onChange("descricao")}
            placeholder="ENTREGA, AVARIA..."
          />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
              Categoria
            </label>
            <select
              data-testid="filter-categoria"
              value={filters.categoria}
              onChange={onChange("categoria")}
              className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
            >
              <option value="">Todas</option>
              {CATEGORIAS.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Data início"
            testId="filter-data-inicio"
            type="date"
            value={filters.data_inicio}
            onChange={onChange("data_inicio")}
          />
          <Field
            label="Data fim"
            testId="filter-data-fim"
            type="date"
            value={filters.data_fim}
            onChange={onChange("data_fim")}
          />
          <div className="flex items-end gap-2">
            <button
              data-testid="apply-filters-btn"
              onClick={applyFilters}
              className="flex-1 bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              <Search size={14} />
              Filtrar
            </button>
            <button
              data-testid="clear-filters-btn"
              onClick={clearFilters}
              className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-3 py-2 text-sm font-medium rounded-md transition-colors"
              title="Limpar filtros"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full text-sm" data-testid="movs-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>NF</th>
                <th>Série</th>
                <th>CNPJ Cliente</th>
                <th>Transportadora</th>
                <th>Cód.</th>
                <th>Descrição</th>
                <th>Status</th>
                <th className="w-16 text-right">—</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center text-[#52525B] py-10">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-[#52525B] py-10">
                    Nenhuma movimentação encontrada com os filtros atuais.
                  </td>
                </tr>
              )}
              {!loading &&
                data.items.map((m, idx) => {
                  const st = statusAccent(m);
                  return (
                    <tr
                      key={m.id}
                      data-testid={`mov-row-${idx}`}
                      onClick={() => openDetails(m)}
                      className="cursor-pointer"
                    >
                      <td className="font-mono-data text-xs text-[#52525B] whitespace-nowrap">
                        {formatDateTime(m.data_ocorrencia)}
                      </td>
                      <td className="font-mono-data font-semibold text-[#09090B]">
                        {m.numero_nota || "—"}
                      </td>
                      <td className="font-mono-data text-xs text-[#52525B]">
                        {m.serie || "—"}
                      </td>
                      <td className="font-mono-data text-xs text-[#52525B]">
                        {formatCNPJ(m.cnpj_cliente)}
                      </td>
                      <td className="text-[#09090B] text-xs">
                        {m.nome_transportadora || "—"}
                      </td>
                      <td className="font-mono-data text-xs font-semibold text-[#09090B]">
                        {m.codigo_ocorrencia || "—"}
                      </td>
                      <td className="text-xs text-[#52525B] max-w-md">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="truncate">{m.descricao_ocorrencia || "—"}</span>
                          {m.descricao_truncada && (
                            <span
                              data-testid={`mov-truncada-${idx}`}
                              className="inline-flex items-center text-amber-700 shrink-0"
                              title="Descrição truncada no arquivo origem"
                            >
                              <span className="text-[9px] uppercase tracking-[0.1em] font-bold ml-1 px-1 py-0.5 bg-amber-50 border border-amber-200 rounded-sm">
                                trunc
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${st.class}`}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td className="text-right">
                        <Eye
                          size={14}
                          className="text-[#52525B] inline-block"
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <OcorrenciaDrawer
        mov={selected}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}

const Field = ({ label, testId, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B] placeholder:text-[#A1A1AA]"
    />
  </div>
);
